import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import {
    ArrowLeft, Check, X, Star, ShoppingBag, TrendingUp, Edit2, Save,
    Eye, Tag, Package, MapPin, Store,
} from "lucide-react";
import { toast } from "sonner";

export default function AdminProductDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [editing, setEditing] = useState(false);
    const [form, setForm] = useState({});
    const [activeImg, setActiveImg] = useState(0);

    async function load() {
        const { data: d } = await api.get(`/admin/products/${id}/full`);
        setData(d);
        const producerPrice = d.product.producer_price != null ? d.product.producer_price : d.product.price * 0.85;
        setForm({
            name: d.product.name,
            description: d.product.description,
            price: d.product.price,
            producer_price: producerPrice,
            stock: d.product.stock,
            category: d.product.category,
            region: d.product.region,
            limited_edition: d.product.limited_edition,
            images: d.product.images || [],
        });
        setActiveImg(0);
    }

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    async function toggleApproval() {
        await api.post(`/admin/products/${id}/toggle-approval`);
        toast.success("Estado atualizado");
        load();
    }
    async function save() {
        await api.patch(`/admin/products/${id}`, {
            ...form,
            price: parseFloat(form.price),
            producer_price: parseFloat(form.producer_price),
            stock: parseInt(form.stock),
        });
        toast.success("Produto atualizado");
        setEditing(false);
        load();
    }

    // Linked price editors: producer + commission = sale price
    function setPrice(sale) {
        const v = parseFloat(sale) || 0;
        const ratio = form.price > 0 ? form.producer_price / form.price : 0.85;
        setForm({ ...form, price: v, producer_price: parseFloat((v * ratio).toFixed(2)) });
    }
    function setProducerPrice(prod) {
        const v = parseFloat(prod) || 0;
        const sale = parseFloat(form.price) || 0;
        if (v > sale) {
            setForm({ ...form, producer_price: v, price: v });
        } else {
            setForm({ ...form, producer_price: v });
        }
    }
    function setCommission(comm) {
        const v = parseFloat(comm) || 0;
        const sale = parseFloat(form.price) || 0;
        const newProducer = parseFloat((sale - v).toFixed(2));
        setForm({ ...form, producer_price: newProducer < 0 ? 0 : newProducer });
    }
    function setImg(i, v) {
        const arr = [...form.images];
        arr[i] = v;
        setForm({ ...form, images: arr });
    }
    function addImg() {
        setForm({ ...form, images: [...form.images, ""] });
    }
    function removeImg(i) {
        const arr = form.images.filter((_, idx) => idx !== i);
        setForm({ ...form, images: arr });
    }

    if (!data) return <p className="text-wine-dark/60 label-overline">A carregar…</p>;
    const { product, producer, reviews, stats } = data;
    const images = product.images?.length > 0 ? product.images : [];

    return (
        <div className="space-y-8">
            <button
                onClick={() => nav("/admin/produtos")}
                className="inline-flex items-center gap-2 label-overline text-wine hover:text-gold-deep transition-colors"
                data-testid="back-products"
            >
                <ArrowLeft className="h-4 w-4" /> Voltar a produtos
            </button>

            {/* Hero: images + key info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Image gallery */}
                <div className="space-y-3">
                    <div className="aspect-[4/5] bg-cream-warm rounded-md overflow-hidden border border-gold-soft/30 relative">
                        {images[activeImg] ? (
                            <img src={images[activeImg]} alt={product.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-wine-dark/30">
                                <Package className="h-16 w-16" strokeWidth={1} />
                            </div>
                        )}
                        {product.limited_edition && (
                            <span className="absolute top-4 left-4 bg-wine-dark text-gold-soft label-overline px-3 py-1.5 rounded">
                                Edição Limitada
                            </span>
                        )}
                    </div>
                    {images.length > 1 && (
                        <div className="grid grid-cols-5 gap-2">
                            {images.map((src, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveImg(i)}
                                    className={`aspect-square rounded overflow-hidden border-2 transition-all ${
                                        activeImg === i ? "border-wine" : "border-transparent opacity-70 hover:opacity-100"
                                    }`}
                                >
                                    <img src={src} alt="" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Info + actions */}
                <div className="space-y-6">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`label-overline px-2 py-1 rounded text-[10px] ${product.approved ? "bg-vineyard/10 text-vineyard" : "bg-gold-soft/20 text-gold-deep"}`}>
                            {product.approved ? "Aprovado" : "Pendente"}
                        </span>
                        {product.stock === 0 && (
                            <span className="label-overline px-2 py-1 rounded text-[10px] bg-destructive/10 text-destructive">
                                Esgotado
                            </span>
                        )}
                        <span className="label-overline px-2 py-1 rounded text-[10px] bg-wine/5 text-wine">
                            {product.category}
                        </span>
                    </div>
                    <h1 className="font-heading text-4xl md:text-5xl text-wine-dark leading-tight">{product.name}</h1>
                    {producer && (
                        <Link
                            to={`/admin/produtores/${producer.id}`}
                            className="inline-flex items-center gap-2 label-overline text-gold-deep hover:text-wine transition-colors"
                            data-testid="product-producer-link"
                        >
                            <Store className="h-3.5 w-3.5" strokeWidth={1.5} />
                            {producer.producer_name}
                        </Link>
                    )}
                    <p className="text-wine-dark/75 leading-relaxed">{product.description}</p>

                    <div className="border-y border-wine-dark/10 py-5 space-y-3">
                        <div className="grid grid-cols-3 gap-4">
                            <div>
                                <span className="label-overline text-vineyard">Valor produtor</span>
                                <p className="font-heading text-xl text-vineyard mt-1">€{((product.producer_price != null ? product.producer_price : product.price * 0.85)).toFixed(2)}</p>
                                <span className="text-[10px] text-wine-dark/50">
                                    {(((product.producer_price != null ? product.producer_price : product.price * 0.85) / product.price) * 100).toFixed(0)}% repasse
                                </span>
                            </div>
                            <div>
                                <span className="label-overline text-gold-deep">Comissão plataforma</span>
                                <p className="font-heading text-xl text-gold-deep mt-1">€{(product.price - (product.producer_price != null ? product.producer_price : product.price * 0.85)).toFixed(2)}</p>
                                <span className="text-[10px] text-wine-dark/50">
                                    {(((product.price - (product.producer_price != null ? product.producer_price : product.price * 0.85)) / product.price) * 100).toFixed(0)}% comissão
                                </span>
                            </div>
                            <div>
                                <span className="label-overline text-wine">Preço de venda</span>
                                <p className="font-heading text-3xl text-wine-dark mt-1">€{product.price.toFixed(2)}</p>
                                <span className="text-[10px] text-wine-dark/50">total no site</span>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4 pt-3 border-t border-wine-dark/5">
                            <Mini label="Stock" value={product.stock} />
                            <Mini label="Região" value={product.region} icon={MapPin} />
                        </div>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <Link to={`/produto/${product.id}`} target="_blank" className="btn-wine-outline text-xs px-5 py-2.5 inline-flex items-center gap-2">
                            <Eye className="h-3.5 w-3.5" /> Ver página pública
                        </Link>
                        <button
                            onClick={toggleApproval}
                            className={`label-overline px-5 py-2.5 rounded-md inline-flex items-center gap-2 text-xs transition-colors ${
                                product.approved
                                    ? "bg-destructive/90 text-cream hover:bg-destructive"
                                    : "bg-vineyard text-cream hover:bg-vineyard/85"
                            }`}
                            data-testid="toggle-approval-product"
                        >
                            {product.approved ? <><X className="h-3.5 w-3.5" /> Suspender</> : <><Check className="h-3.5 w-3.5" /> Aprovar</>}
                        </button>
                        {!editing && (
                            <button onClick={() => setEditing(true)} className="btn-wine text-xs px-5 py-2.5 inline-flex items-center gap-2" data-testid="edit-product">
                                <Edit2 className="h-3.5 w-3.5" /> Editar
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Kpi icon={TrendingUp} label="Receita" value={`€${stats.revenue.toFixed(2)}`} highlight />
                <Kpi icon={ShoppingBag} label="Vendidos" value={stats.units_sold} sub={`${stats.orders_count} encomendas`} />
                <Kpi icon={Star} label="Avaliação" value={stats.avg_rating || "—"} sub={`${stats.reviews_count} reviews`} />
                <Kpi icon={Tag} label="Criado" value={product.created_at ? new Date(product.created_at).toLocaleDateString("pt-PT") : "—"} />
            </div>

            {/* Edit form */}
            {editing && (
                <div className="bg-white border border-gold-soft/30 rounded-md p-6 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="font-heading text-2xl text-wine-dark">Editar produto</h2>
                        <div className="flex gap-2">
                            <button onClick={() => setEditing(false)} className="text-xs px-3 py-2 text-wine-dark/60 hover:text-wine-dark">
                                Cancelar
                            </button>
                            <button onClick={save} className="btn-wine text-xs px-4 py-2 inline-flex items-center gap-2" data-testid="save-product">
                                <Save className="h-3 w-3" /> Guardar
                            </button>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <EditField label="Nome" value={form.name} set={(v) => setForm({ ...form, name: v })} />
                        <EditField label="Stock" value={form.stock} set={(v) => setForm({ ...form, stock: v })} type="number" />
                        <div className="md:col-span-2 bg-cream rounded-md p-4 border border-gold-soft/40">
                            <div className="flex items-center justify-between mb-3">
                                <span className="label-overline text-wine">Preços (editáveis e ligados)</span>
                                <span className="text-[10px] text-wine-dark/50">Editar um campo recalcula os outros</span>
                            </div>
                            <div className="grid grid-cols-3 gap-3">
                                <EditField
                                    label="Valor produtor (€)"
                                    value={form.producer_price}
                                    set={setProducerPrice}
                                    type="number"
                                    testId="edit-producer-price"
                                    color="vineyard"
                                />
                                <EditField
                                    label="Comissão plataforma (€)"
                                    value={(parseFloat(form.price) - parseFloat(form.producer_price || 0)).toFixed(2)}
                                    set={setCommission}
                                    type="number"
                                    testId="edit-commission"
                                    color="gold-deep"
                                />
                                <EditField
                                    label="Preço de venda (€)"
                                    value={form.price}
                                    set={setPrice}
                                    type="number"
                                    testId="edit-sale-price"
                                    color="wine"
                                />
                            </div>
                            <div className="mt-3 text-[11px] text-wine-dark/55 flex items-center justify-between">
                                <span>Repasse produtor: <strong className="text-vineyard">{form.price > 0 ? ((form.producer_price / form.price) * 100).toFixed(1) : "0"}%</strong></span>
                                <span>Comissão plataforma: <strong className="text-gold-deep">{form.price > 0 ? (((form.price - form.producer_price) / form.price) * 100).toFixed(1) : "0"}%</strong></span>
                            </div>
                        </div>
                        <EditField label="Categoria" value={form.category} set={(v) => setForm({ ...form, category: v })} />
                        <EditField label="Região" value={form.region} set={(v) => setForm({ ...form, region: v })} />
                        <label className="flex items-center gap-2 mt-7">
                            <input type="checkbox" checked={form.limited_edition} onChange={(e) => setForm({ ...form, limited_edition: e.target.checked })} className="accent-wine" />
                            <span className="text-sm">Edição limitada</span>
                        </label>
                        <label className="block md:col-span-2">
                            <span className="label-overline text-wine-dark">Descrição</span>
                            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="mt-2 w-full bg-transparent border border-wine-dark/20 rounded-md px-4 py-3 text-sm focus:border-wine" />
                        </label>
                        <div className="md:col-span-2">
                            <span className="label-overline text-wine-dark">Imagens (URLs)</span>
                            <div className="mt-2 space-y-2">
                                {(form.images || []).map((u, i) => (
                                    <div key={i} className="flex gap-2">
                                        <input value={u} onChange={(e) => setImg(i, e.target.value)} className="flex-1 bg-transparent border border-wine-dark/20 rounded-md px-3 py-2 text-sm focus:border-wine" />
                                        <button type="button" onClick={() => removeImg(i)} className="text-destructive">
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                                <button type="button" onClick={addImg} className="label-overline text-wine hover:text-gold-deep text-xs">
                                    + Adicionar imagem
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Reviews */}
            <div className="bg-white border border-gold-soft/30 rounded-md p-6 shadow-sm">
                <span className="label-overline text-wine/60">Feedback</span>
                <h2 className="font-heading text-2xl text-wine-dark mt-1 mb-5">Reviews dos clientes</h2>
                {reviews.length === 0 ? (
                    <p className="text-wine-dark/60 text-sm">Sem reviews ainda.</p>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {reviews.map((r) => (
                            <div key={r.id} className="border border-wine-dark/5 rounded-md p-4" data-testid={`admin-review-${r.id}`}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-sm font-semibold text-wine-dark">{r.user_name}</span>
                                    <div className="flex gap-0.5">
                                        {[1, 2, 3, 4, 5].map((n) => (
                                            <Star key={n} className={`h-3 w-3 ${n <= r.rating ? "fill-gold-soft text-gold-soft" : "text-wine-dark/20"}`} />
                                        ))}
                                    </div>
                                </div>
                                <p className="text-sm text-wine-dark/75 italic">"{r.comment}"</p>
                                <p className="text-xs text-wine-dark/50 mt-2">{new Date(r.created_at).toLocaleDateString("pt-PT")}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function Mini({ label, value, icon: Icon, large }) {
    return (
        <div>
            <div className="flex items-center gap-1.5 mb-1">
                {Icon && <Icon className="h-3 w-3 text-wine/50" strokeWidth={1.5} />}
                <span className="label-overline text-wine-dark/60">{label}</span>
            </div>
            <p className={`font-heading text-wine-dark ${large ? "text-2xl" : "text-base"}`}>{value}</p>
        </div>
    );
}

function Kpi({ icon: Icon, label, value, sub, highlight }) {
    return (
        <div className={`p-5 rounded-md border shadow-sm ${highlight ? "bg-wine-dark text-cream border-wine-dark" : "bg-white border-gold-soft/30"}`}>
            <div className="flex justify-between items-start">
                <span className={`label-overline ${highlight ? "text-gold-soft" : "text-wine/60"}`}>{label}</span>
                <Icon className={`h-4 w-4 ${highlight ? "text-gold-soft" : "text-wine/40"}`} strokeWidth={1.5} />
            </div>
            <p className="font-heading text-3xl mt-2 leading-none">{value}</p>
            {sub && <span className={`text-xs mt-2 inline-block ${highlight ? "text-cream/60" : "text-wine-dark/55"}`}>{sub}</span>}
        </div>
    );
}

function EditField({ label, value, set, type = "text", testId, color }) {
    const colorMap = {
        vineyard: "border-vineyard/40 focus:border-vineyard",
        "gold-deep": "border-gold-deep/40 focus:border-gold-deep",
        wine: "border-wine/40 focus:border-wine",
    };
    const cls = color ? colorMap[color] : "border-wine-dark/20 focus:border-wine";
    return (
        <label className="block">
            <span className="label-overline text-wine-dark">{label}</span>
            <input
                type={type}
                step={type === "number" ? "0.01" : undefined}
                value={value}
                onChange={(e) => set(e.target.value)}
                className={`mt-2 w-full bg-white border ${cls} rounded-md px-4 py-3 text-sm`}
                data-testid={testId}
            />
        </label>
    );
}
